import { StandardEvents } from '@shopify/events';

/**
 * Haelt den Belohnungspreis auf der Produktseite mit dem Warenkorb im Takt.
 *
 * Beim Seitenaufruf rechnet Liquid den Zustand aus ('snippets/gift-price.liquid').
 * Aendert sich der Warenkorb danach ohne Neuladen – etwa weil im Drawer ein
 * T-Shirt entfernt wird, waehrend die Schmuckseite offen ist –, stuende dort
 * sonst weiter ein Preis, den die Kasse nicht mehr gibt.
 *
 * Warum nicht die Section neu rendern? Der Preisblock haengt an der gewaehlten
 * Variante; ein Neuaufbau muesste die mitschleppen und wuerde fuer eine Zahl den
 * kompletten Produktbereich holen. Die Schwellen und beide Stufenpreise stehen
 * bereits als Attribute im Markup – es fehlt nur die Stueckzahl im Warenkorb.
 */

/**
 * Ein einziger Warenkorb-Abruf pro Aktualisierung, auch wenn mehrere
 * <gift-price> auf der Seite stehen (Preisblock und Sticky-Leiste).
 *
 * @type {Promise<any> | null}
 */
let pendingCart = null;

/** @returns {Promise<any>} */
function fetchCart() {
  pendingCart ??= fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
    .then((response) => response.json())
    .finally(() => {
      pendingCart = null;
    });

  return pendingCart;
}

class GiftPrice extends HTMLElement {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    const { signal } = this.#abortController;

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate, { signal });

    // Zurueck-Navigation aus dem bfcache zeigt den Stand von vorhin – der
    // Warenkorb kann sich auf der Zwischenseite geaendert haben.
    window.addEventListener('pageshow', this.#handlePageShow, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  /** @param {PageTransitionEvent} event */
  #handlePageShow = (event) => {
    if (event.persisted) this.#refresh();
  };

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #handleCartLinesUpdate = (event) => {
    // Das Event feuert, bevor der Warenkorb steht. Erst sein Promise sagt, dass
    // die Zeilen wirklich geschrieben sind – ohne das Warten liest der Abruf
    // unten noch den alten Stand.
    const settled = event.promise ?? Promise.resolve();

    settled.then(() => this.#refresh()).catch(this.#handleError);
  };

  #refresh() {
    return fetchCart()
      .then((cart) => this.#update(cart))
      .catch(this.#handleError);
  }

  /** @param {any} error */
  #handleError = (error) => {
    if (error?.name !== 'AbortError') console.warn('[gift-price] Warenkorb nicht lesbar:', error);
  };

  /**
   * @param {{ items?: Array<{ product_id: number, quantity: number }> }} cart
   */
  #update(cart) {
    const triggerIds = this.dataset.triggerIds ?? ',';
    const rewardIds = this.dataset.rewardIds ?? ',';
    const tier1Qty = Number(this.dataset.tier1Qty) || 1;
    const tier2Qty = Math.max(Number(this.dataset.tier2Qty) || tier1Qty, tier1Qty);

    let triggerCount = 0;
    let rewardCount = 0;

    for (const item of cart?.items ?? []) {
      // `,id,` an beiden Enden, damit `,12,` nicht in `,123,` trifft.
      const needle = `,${item.product_id},`;

      if (triggerIds.includes(needle)) triggerCount += item.quantity;
      if (rewardIds.includes(needle)) rewardCount += item.quantity;
    }

    // Der Rabatt gilt fuer genau ein Schmuckstueck: liegt schon eines im
    // Warenkorb, ist er aufgebraucht.
    let tier = 0;
    if (rewardCount === 0) {
      if (triggerCount >= tier2Qty) tier = 2;
      else if (triggerCount >= tier1Qty) tier = 1;
    }

    if (tier > 0) {
      const amount = tier === 2 ? this.dataset.priceTier2 : this.dataset.priceTier1;
      const amountElement = this.querySelector('.gift-price__amount');

      if (amount && amountElement) amountElement.textContent = amount;
    }

    this.dataset.unlocked = tier > 0 ? 'true' : 'false';
  }
}

if (!customElements.get('gift-price')) {
  customElements.define('gift-price', GiftPrice);
}
